import type { OrderLine } from "./api";
import type { PaymentRow } from "./staff-api";
import { toCsv } from "./csv";
import type { CsvColumn } from "./csv";
import { formatOrderStatus } from "./formatters";

export const UNKNOWN_VENDOR = "Unknown vendor";

const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function localMidnight(day: string, addDays = 0): Date | null {
  if (!DAY_PATTERN.test(day)) return null;
  const [y, m, d] = day.split("-").map(Number);
  const date = new Date(y!, m! - 1, d! + addDays, 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** A local calendar date as YYYY-MM-DD. */
export function dayString(date: Date): string {
  const p = (n: number): string => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
}

/** The 30 days up to and including today, as the local dates the inputs hold. */
export function defaultDayRange(now: Date = new Date()): {
  start: string;
  end: string;
} {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
  return { start: dayString(start), end: dayString(now) };
}

/** The API takes ISO instants, end exclusive. The end DAY is included, so the
 *  window ends at local midnight of the day after it. Null when either day is
 *  missing or malformed, or the range runs backwards. */
export function rangeToInstants(startDay: string, endDay: string): { start: string; end: string } | null {
  const start = localMidnight(startDay);
  const end = localMidnight(endDay, 1);
  if (!start || !end || end.getTime() <= start.getTime()) return null;
  return { start: start.toISOString(), end: end.toISOString() };
}

export interface ProductTotals {
  bcbQty: number;
  bcbSubtotal: number;
  badgeQty: number;
  badgeSubtotal: number;
}

const cents = (n: number): number => Math.round(n * 100) / 100;

/** Per-product quantity and subtotal from an order's lines, so accounting can
 *  journal-split a mixed payment. Null when the order has no lines. */
export function productTotals(lines: OrderLine[] | null): ProductTotals | null {
  if (!lines) return null;
  const totals: ProductTotals = {
    bcbQty: 0,
    bcbSubtotal: 0,
    badgeQty: 0,
    badgeSubtotal: 0,
  };
  for (const line of lines) {
    if (line.product === "bcb") {
      totals.bcbQty += line.quantity;
      totals.bcbSubtotal += line.quantity * line.unitPrice;
    } else {
      totals.badgeQty += line.quantity;
      totals.badgeSubtotal += line.quantity * line.unitPrice;
    }
  }
  totals.bcbSubtotal = cents(totals.bcbSubtotal);
  totals.badgeSubtotal = cents(totals.badgeSubtotal);
  return totals;
}

/** Accounting's import format is still an open question (spec open question 4);
 *  Celeste and Becky may redefine these columns. */
export const PAYMENT_CSV_COLUMNS: CsvColumn[] = [
  { key: "date", header: "Date" },
  { key: "paidAtUtc", header: "Paid at (UTC)" },
  { key: "vendor", header: "Vendor" },
  { key: "orderType", header: "Order type" },
  { key: "amount", header: "Amount" },
  { key: "vancoPaymentId", header: "Vanco payment ID" },
  { key: "cardLast4", header: "Card last 4" },
  { key: "status", header: "Status" },
  { key: "flagged", header: "Flagged" },
  { key: "bcbQty", header: "BCB qty" },
  { key: "bcbSubtotal", header: "BCB subtotal" },
  { key: "badgeQty", header: "Badge qty" },
  { key: "badgeSubtotal", header: "Badge subtotal" },
];

export function paymentsCsv(payments: PaymentRow[]): string {
  const rows = payments.map((p) => {
    const totals = productTotals(p.lines);
    return {
      // Local calendar day, as the table and the date filter show it; the UTC
      // instant rides alongside so nothing is lost.
      date: dayString(new Date(p.date)),
      paidAtUtc: p.date,
      vendor: p.vendorCompanyName ?? UNKNOWN_VENDOR,
      orderType: p.orderTypeLabel,
      amount: p.amount,
      vancoPaymentId: p.vancoPaymentID,
      cardLast4: p.cardLast4,
      status: formatOrderStatus(p.status),
      flagged: p.reconcileNeeded ? "Yes" : "No",
      bcbQty: totals?.bcbQty ?? "",
      bcbSubtotal: totals?.bcbSubtotal ?? "",
      badgeQty: totals?.badgeQty ?? "",
      badgeSubtotal: totals?.badgeSubtotal ?? "",
    };
  });
  return toCsv(rows, PAYMENT_CSV_COLUMNS);
}

/** Excel opens a BOM-less CSV in the legacy code page and garbles accented
 *  vendor names; the byte order mark tells it the file is UTF-8. */
const UTF8_BOM = "\uFEFF";

/** Hands the CSV to the browser as a download. A Blob object URL, no storage. */
export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([UTF8_BOM + csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
